import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { styled } from 'styled-components';
import { DateInput } from './DateInput';
import { dateContainerStyle } from './DateInput.styles';
import { PlayFlatpickr } from './PlayFlatpickr';
import { calendarWidth } from './PlayFlatpickr.styles';
import { Colors } from '../../../../play-core/styles/Colors';
import { Fonts } from '../../../../play-core/styles/Fonts';
import { shortDateFormat } from '../../utils/date-formatter';
import { TransparentButton } from '../ClickableComponents/Button';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DatePicker = ({
  date,
  defaultDate,
  restrictedRange = {},
  selectedRange = {},
  isOpen,
  keepOpen = false,
  fullWidth = false,
  stateInformation: externalStateInformation,
  showCalendarIcon = false,
  markedDates = {},
  inputStyle = 'Single',
  quickSelectDate,
  quickSelectText,
  onChange,
  onOpenChanged,
  onValidityChanged = () => undefined,
  prependedContent,
  fireChangesWhenUndefined = true,
  expandInput,
  flyoutStyle = 'default',
  flyoutPosition = 'left',
  isDisabled = false
}) => {
  const {
    t
  } = useTranslation();
  const datePickerRef = useRef(null);
  useClickAway(datePickerRef, () => {
    if (!keepOpen) {
      onOpenChanged(false);
    }
    if (!(fireChangesWhenUndefined || inputDate) && isOpen) {
      setInputDate(date);
    }
  });

  // to handle allowUndefinedChanges = false
  const [inputDate, setInputDate] = useState(date);

  // Use a separate date for the flatpickr instance which will be set to undefined
  // when the user enters an invalid date manually.
  const [flatpickrDate, setFlatpickrDate] = useState(date);
  const [validity, setValidity] = useState('Valid');
  const [alignFlyoutRight, setAlignFlyoutRight] = useState(flyoutPosition === 'right');
  const message = validity !== 'Valid' ? `date.error.${validity}` : externalStateInformation?.text;
  const hasError = validity !== 'Valid' || externalStateInformation?.markFieldAsError;
  useEffect(() => setInputDate(date), [date]);
  useEffect(() => setFlatpickrDate(date), [date]);
  const emitChanges = (value, source) => {
    setInputDate(value);
    if (fireChangesWhenUndefined || value) {
      onChange(value, source);
    }
  };

  // A hacky way to figure out if the focus is still within this component
  // (namely: the Flatpickr flyout) after the input was blurred.
  const onInputBlur = () => {
    setTimeout(() => {
      if (datePickerRef.current && !keepOpen) {
        const focusWithin = datePickerRef.current.contains(document.activeElement);
        if (isOpen !== focusWithin) {
          onOpenChanged(focusWithin);
        }
      }
    }, 0);
  };
  return /*#__PURE__*/_jsxs(StyledDatePicker, {
    inputStyle: inputStyle,
    ref: datePickerRef,
    fullWidth: fullWidth,
    hasMessage: !!message,
    children: [/*#__PURE__*/_jsx(DateInput, {
      date: inputDate,
      restrictedRange: restrictedRange,
      requestFocus: isOpen,
      showError: hasError,
      fullWidth: fullWidth,
      expand: expandInput,
      showCalendarIcon: showCalendarIcon,
      inputStyle: inputStyle,
      onChange: value => emitChanges(value, 'Input'),
      onValidityChanged: value => {
        onValidityChanged(value);
        setValidity(value);
        if (value !== 'Valid') {
          setFlatpickrDate(undefined);
        }
      },
      onFocus: () => {
        onOpenChanged(true);
        if (datePickerRef.current && flyoutPosition !== 'right') {
          const viewWidth = document.documentElement.clientWidth;
          const dropdownLeft = datePickerRef.current.getBoundingClientRect().left;
          setAlignFlyoutRight(dropdownLeft + calendarWidth[flyoutStyle] > viewWidth);
        }
      },
      onBlur: onInputBlur,
      isDisabled: isDisabled
    }), isOpen && /*#__PURE__*/_jsx(PlayFlatpickr, {
      date: flatpickrDate,
      defaultDate: defaultDate,
      restrictedRange: restrictedRange,
      selectedRange: selectedRange,
      fullWidth: fullWidth,
      keepOpen: keepOpen,
      alignRight: alignFlyoutRight,
      markedDates: markedDates,
      flyoutStyle: flyoutStyle,
      prependedContent: prependedContent || /*#__PURE__*/_jsx(Fragment, {
        children: quickSelectDate && quickSelectText && quickSelectDate !== date && /*#__PURE__*/_jsx(TransparentButton, {
          type: "text",
          onClick: () => emitChanges(quickSelectDate, 'DatePicker'),
          text: quickSelectText,
          expand: true
        })
      }),
      onChange: value => emitChanges(value, 'DatePicker')
    }), message && /*#__PURE__*/_jsx(ErrorMessage, {
      "aria-live": "polite",
      children: t(message, {
        minDate: shortDateFormat(restrictedRange.from),
        maxDate: shortDateFormat(restrictedRange.to)
      })
    })]
  });
};
export const StyledDatePicker = styled.div.withConfig({
  displayName: "DatePicker__StyledDatePicker",
  componentId: "sc-1gwd4ng-0"
})(["position:", ";padding-bottom:", ";flex:1;", ";"], props => props.fullWidth ? 'unset' : 'relative', props => props.hasMessage ? '20px' : '0', dateContainerStyle);
const ErrorMessage = styled.span.withConfig({
  displayName: "DatePicker__ErrorMessage",
  componentId: "sc-1gwd4ng-1"
})(["position:absolute;white-space:nowrap;color:", ";", ";line-height:22px;"], Colors.ERROR, Fonts.regular(12));